/**
 * COMPONENTE LAZYSECTION - SISTEMA DE ANIMACIONES VIEWPORT-TRIGGERED
 * 
 * Este componente maneja las animaciones que se activan cuando las secciones
 * entran en el viewport del usuario. Utiliza Intersection Observer API
 * para detectar visibilidad y Framer Motion para las animaciones.
 * 
 * TIPOS DE ANIMACIÓN PERMITIDOS:
 * ✅ fadeUp - Desvanecimiento desde abajo (SEGURO)
 * ✅ fadeIn - Desvanecimiento simple (SEGURO)  
 * ✅ scale - Escala desde centro (SEGURO)
 * ❌ fadeLeft/fadeRight - PROHIBIDO (causa scroll horizontal)
 * 
 * PARÁMETROS:
 * @param {React.ReactNode} children - Componente a animar
 * @param {number} threshold - Porcentaje visible para activar (0.1 = 10%)
 * @param {boolean} triggerOnce - Si animar solo una vez (true por defecto)
 * @param {string} animationType - Tipo de animación (fadeUp, fadeIn, scale)
 * @param {number} delay - Retraso en segundos antes de la animación
 * 
 * USO TÍPICO:
 * <LazySection animationType="fadeUp" delay={0.1}>
 *   <MiComponente />
 * </LazySection>
 */

import { useInView } from 'react-intersection-observer';
import { Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const LazySection = ({ 
  children, 
  threshold = 0.1,        // 10% visible para activar
  triggerOnce = true,     // Animar solo una vez
  animationType = 'fadeUp', // Tipo de animación por defecto
  delay = 0.2,            // Retraso por defecto
  ...boxProps             // Props adicionales para el Box
}) => {
  // Hook de Intersection Observer para detectar visibilidad
  const { ref, inView } = useInView({
    threshold,
    triggerOnce,
  });

  /**
   * VARIANTES DE ANIMACIÓN
   * 
   * Define los estados inicial (hidden) y final (visible) para cada tipo.
   * Todas las animaciones usan easing suave y duraciones optimizadas.
   * 
   * REGLA CRÍTICA: Solo transformaciones verticales/opacidad/escala
   * para evitar scroll horizontal indeseado.
   */
  const animationVariants = {
    // Desvanecimiento desde abajo hacia arriba
    fadeUp: {
      hidden: { 
        opacity: 0, 
        y: 30,  // Desplazamiento vertical inicial
        transition: { duration: 0.6, ease: "easeOut" }
      },
      visible: { 
        opacity: 1, 
        y: 0,   // Posición final
        transition: { duration: 0.8, ease: "easeOut", delay }
      }
    },
    
    // Escala desde centro con desvanecimiento
    scale: {
      hidden: { 
        opacity: 0, 
        scale: 0.9, // Ligeramente reducido
        transition: { duration: 0.6, ease: "easeOut" }
      },
      visible: { 
        opacity: 1, 
        scale: 1,   // Tamaño original
        transition: { duration: 0.8, ease: "easeOut", delay }
      }
    },
    
    // Desvanecimiento simple sin movimiento
    fadeIn: {
      hidden: { 
        opacity: 0,
        transition: { duration: 0.6, ease: "easeOut" }
      },
      visible: { 
        opacity: 1,
        transition: { duration: 0.8, ease: "easeOut", delay }
      }
    }
  };

  return (
    <Box
      ref={ref}
      as={motion.div}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={animationVariants[animationType]}
      width="100%"
      {...boxProps}
    >
      {children}
    </Box>
  );
};

export default LazySection;
